import React, { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  limit,
  onSnapshot
} from "firebase/firestore";
import {
  Calendar,
  Clock,
  Timer,
  Cpu,
  CheckCircle2,
  XCircle,
  Scan,
  Radar,
  Monitor,
  UserRound,
  Mail,
  Phone,
} from "lucide-react";
import { getAuth } from "firebase/auth";
import { createPortal } from "react-dom";

/** -----------------------------
 * Constants / helpers
 * ------------------------------*/
const OPEN = 11; // 11:00
const CLOSE = 19; // 19:00
const PCS = ["PC1", "PC2", "PC3", "PC4", "PC5"];
const RIG_POS = {
  PC1: { x: 15, y: 25 },
  PC2: { x: 50, y: 18 },
  PC3: { x: 85, y: 25 },
  PC4: { x: 30, y: 48 },
  PC5: { x: 70, y: 48 },
};
const NEON = "#D4FF00";
const RED = "#ef4444";     // Tailwind red-500
const YELLOW = "#facc15";  // Tailwind yellow-400

const pad = (n) => String(n).padStart(2, "0");
const hhmmToMinutes = (s) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};
const label = (h) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:00 ${ampm}`;
};
const between = (open, close, start, end) => start >= open && end <= close && start < end;
const toISO = (dateObj) => {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const addDays = (dateObj, n) => {
  const d = new Date(dateObj);
  d.setDate(d.getDate() + n);
  return d;
};

// --- responsive helper ---
function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}


/** Low-performance detection */
function useLowPerf() {
  const [low, setLow] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    const dm = navigator.deviceMemory || 4; // heuristics
    setLow(reduced || dm <= 4);
  }, []);
  return low;
}

/** -----------------------------
 * Component
 * ------------------------------*/
export default function BookingArena() {
  const auth = getAuth();
  const user = auth.currentUser || null;
  const lowPerf = useLowPerf();
  const [isPending, startTransition] = useTransition();

  // inputs
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState(1);

  // guest info
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  // selection
  const [availablePCs, setAvailablePCs] = useState(PCS);
  const [selectedPC, setSelectedPC] = useState("");

  // UX
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);

  const mdUp = useMediaQuery("(min-width: 768px)");
  const lgUp = useMediaQuery("(min-width: 1024px)");
  const iconScale = lgUp ? 0.78 : mdUp ? 0.84 : 1; // shrink a bit on md/lg

  // derived
  const endTime = useMemo(() => {
    if (!startTime) return "";
    const h = parseInt(startTime.split(":")[0], 10) + duration;
    return `${pad(h)}:00`;
  }, [startTime, duration]);

  const timeSlots = useMemo(() => {
    const slots = [];
    for (let h = OPEN; h < CLOSE; h++) slots.push(`${pad(h)}:00`);
    return slots;
  }, []);



  //** Availability (live) */
  useEffect(() => {
    if (!date || !startTime || !endTime) return;

    const qRef = query(collection(db, "bookings"), where("date", "==", date), limit(500));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const taken = new Set();
        snap.forEach((d) => {
          const b = d.data();
          // overlap if NOT (end <= start || start >= end)
          if (!(b.endTime <= startTime || b.startTime >= endTime)) taken.add(b.pcId);
        });
        const free = PCS.filter((pc) => !taken.has(pc));
        // mark as non-urgent to keep UI responsive
        startTransition(() => {
          setAvailablePCs(free);
          setSelectedPC((prev) => (free.includes(prev) ? prev : ""));
        });
      },
      (err) => console.error("Availability snapshot error:", err)
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, date, startTime, endTime]);

  /** Book */
  async function book() {
    setToast("");
    if (!date || !startTime || !endTime || !selectedPC) {
      setToast("⚠️ Select date, start, duration, and a rig.");
      return;
    }
    const isGuest = !user;
    if (
      (isGuest && (!guestName || !guestEmail || !guestPhone)) ||
      (!isGuest && !guestPhone)
    ) {
      setToast("⚠️ Please enter all required details, including phone.");
      return;
    }
    const sMin = hhmmToMinutes(startTime);
    const eMin = hhmmToMinutes(endTime);
    if (!between(OPEN * 60, CLOSE * 60, sMin, eMin)) {
      setToast("⚠️ Pick a time between 11:00 and 19:00.");
      return;
    }

    try {
      setBusy(true);
      const payload = {
        pcId: selectedPC,
        date,
        startTime,
        endTime,
        createdAt: serverTimestamp(),
      };
      if (user) {
        payload.userId = user.uid;
        payload.userEmail = user.email || null;
        payload.userName = user.displayName || guestName || "User";
        payload.userPhone = guestPhone; // 👈 always required
      } else {
        payload.guestName = guestName;
        payload.guestEmail = guestEmail;
        payload.guestPhone = guestPhone;
      }

      await addDoc(collection(db, "bookings"), payload);

      setToast(`✅ ${selectedPC} booked for ${date}, ${startTime}–${endTime}. See you soon!`);
      setSelectedPC("");
      if (!user) {
        setGuestName("");
        setGuestEmail("");
        setGuestPhone("");
      }
    } catch (e) {
      console.error(e);
      setToast("❌ Booking failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="relative min-h-[100dvh] overflow-hidden bg-black text-white">
      {/* Neon stage (throttled / reduced on low-perf) */}
      <NeonStage low={lowPerf} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-10 space-y-10">
        {/* Title */}
        <header className="text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#a9ffa2]/70">
            <Scan className="w-4 h-4 shrink-0" />
            <span>RGC // Holo-Booking Interface</span>
          </div>
          <h1 className="mt-3 text-4xl md:text-6xl font-extrabold tracking-tight">
            Deploy to <span className="text-[--neon] drop-shadow-[0_0_18px_rgba(212,255,0,.36)]">Battle Station</span>
          </h1>
          <p className="mt-3 text-neutral-400">
            Open <span className="text-[--neon] font-semibold">11:00–19:00</span> · Five high-end rigs · Reserve in seconds
          </p>
        </header>

        {/* HUD */}
        <section
          className={`relative rounded-2xl border border-white/10 ${lowPerf ? "bg-white/5" : "bg-white/5 backdrop-blur-xl"
            } p-6 md:p-7 shadow-[0_0_40px_rgba(212,255,0,.08)] space-y-5`}
        >
          <HUDChrome />

          <div className="grid gap-4 md:grid-cols-3">
            <HUDField icon={<Calendar className="w-4 h-4 text-[--neon] shrink-0" />} label="Date">
              <NeonDatePicker value={date} onChange={setDate} />
            </HUDField>

            <HUDField icon={<Clock className="w-4 h-4 text-[--neon] shrink-0" />} label="Start">
              <select
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-lg bg-black/60 border border-white/10 p-2 text-white focus:ring-2 focus:ring-[--neon] outline-none"
              >
                <option value="">Select</option>
                {timeSlots.map((t) => (
                  <option key={t} value={t}>
                    {t} ({label(parseInt(t.slice(0, 2), 10))})
                  </option>
                ))}
              </select>
            </HUDField>

            <HUDField icon={<Timer className="w-4 h-4 text-[--neon] shrink-0" />} label="Duration">
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full rounded-lg bg-black/60 border border-white/10 p-2 text-white focus:ring-2 focus:ring-[--neon] outline-none"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
                  <option
                    key={h}
                    value={h}
                    disabled={parseInt((startTime || "11:00").slice(0, 2), 10) + h > CLOSE}
                  >
                    {h} hour{h > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </HUDField>
          </div>

          {/* Guest details */}
          {!user ? (
            <div className="grid gap-4 md:grid-cols-3">
              <HUDField icon={<UserRound className="w-4 h-4 text-[--neon] shrink-0" />} label="Your name">
                <input
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Player Name"
                  className="w-full rounded-lg bg-black/60 border border-white/10 p-2 text-white placeholder:text-neutral-500 focus:ring-2 focus:ring-[--neon] outline-none"
                />
              </HUDField>
              <HUDField icon={<Mail className="w-4 h-4 text-[--neon] shrink-0" />} label="Email">
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg bg-black/60 border border-white/10 p-2 text-white placeholder:text-neutral-500 focus:ring-2 focus:ring-[--neon] outline-none"
                />
              </HUDField>
              <HUDField icon={<Phone className="w-4 h-4 text-[--neon] shrink-0" />} label="Phone">
                <input
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="w-full rounded-lg bg-black/60 border border-white/10 p-2 text-white placeholder:text-neutral-500 focus:ring-2 focus:ring-[--neon] outline-none"
                />
              </HUDField>
            </div>
          ) : (
            <div className="text-sm text-neutral-300 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-black/60 border border-white/10">
                <UserRound className="w-4 h-4 text-[--neon]" />
              </span>
              Booking as{" "}
              <span className="font-semibold text-white">
                {user.displayName || user.email || "Signed-in user"}
              </span>
              <span className="text-neutral-500"> · Phone: </span>
              <input
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                placeholder="Phone Number"
                className="ml-2 rounded bg-black/60 border border-white/10 px-2 py-1 text-white text-sm focus:ring-2 focus:ring-[--neon] outline-none"
              />
            </div>
          )}

          {/* Timeline pill */}
          <div className="text-xs text-neutral-400">
            {startTime ? (
              <span className="inline-flex items-center gap-2 bg-black/40 border border-white/10 rounded-full px-3 py-1">
                <Radar className="w-3.5 h-3.5 text-[--neon]" />
                Window: <b className="text-white ml-1">{startTime}</b> →{" "}
                <b className="text-white">{endTime || "…"} </b>
              </span>
            ) : (
              <span className="opacity-70">Select start time to preview your session window.</span>
            )}
          </div>
        </section>

        {/* Floor */}
        <section
          className={`relative rounded-2xl border border-white/10 ${lowPerf ? "bg-white/5" : "bg-white/5 backdrop-blur-xl"
            } p-5 md:p-7 shadow-[0_0_40px_rgba(212,255,0,.08)]`}
        >
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-black/60 border border-white/10">
                <Cpu className="w-4 h-4 text-[--neon]" />
              </span>
              <div>
                <div className="text-sm text-neutral-300 font-semibold tracking-wide">Café Floor Plan</div>
                <div className="text-xs text-neutral-500">Tap a pod to select your rig</div>
              </div>
            </div>
            <Legend />
          </div>

          <div className="relative rounded-xl border border-white/10 overflow-visible">
            <MemoFloorSVG
              availablePCs={availablePCs}
              selectedPC={selectedPC}
              onSelect={setSelectedPC}
              date={date}
              startTime={startTime}
              endTime={endTime}
              iconScale={iconScale}
            />
            {!lowPerf && (
              <div className="pointer-events-none absolute inset-0 mix-blend-screen opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,.35)_1px,transparent_1px)] bg-[length:100%_3px]" />
            )}
          </div>
        </section>

        {/* Confirm bar */}
        <section className="sticky bottom-4">
          <div
            className={`rounded-2xl border border-white/10 ${lowPerf ? "bg-white/5" : "bg-white/5 backdrop-blur-xl"
              } p-4 shadow-[0_0_40px_rgba(212,255,0,.08)] flex flex-col md:flex-row items-center gap-4 md:gap-6`}
          >
            <div className="flex-1 text-center md:text-left text-sm text-neutral-300">
              {selectedPC && startTime && endTime ? (
                <>
                  <span className="text-white font-semibold">{selectedPC}</span> — {date || "YYYY-MM-DD"} ·{" "}
                  <span className="text-white">{startTime}</span> → <span className="text-white">{endTime}</span>
                </>
              ) : (
                <>Pick date/time and tap a rig on the map to arm the booking.</>
              )}
            </div>

            <button
              onClick={book}
              disabled={busy || !date || !startTime || !endTime || !selectedPC}
              className="px-6 py-3 rounded-xl font-extrabold tracking-wide flex items-center gap-2 bg-[--neon] text-black shadow-[0_0_25px_rgba(212,255,0,.5)] hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ WebkitTapHighlightColor: "transparent" }}
            >
              <CheckCircle2 className="w-5 h-5" />
              {isPending ? "HOLD…" : "DEPLOY PLAYER"}
            </button>
          </div>

          {toast && (
            <div
              className={`mt-3 flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${toast.startsWith("✅") ? "bg-green-900/40 text-green-300" : "bg-red-900/40 text-red-300"
                }`}
            >
              {toast.startsWith("✅") ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              {toast}
            </div>
          )}
        </section>
      </div>

      <style>{`
        :root { --neon: ${NEON}; }
        @keyframes floaty { 0%{transform:translateY(0)} 50%{transform:translateY(-3px)} 100%{transform:translateY(0)} }
      `}</style>
    </main>
  );
}

/** -----------------------------
 * Neon Stage (throttled parallax)
 * ------------------------------*/
function NeonStage({ low }) {
  const ref = useRef(null);

  useEffect(() => {
    if (low) return; // disable on low-perf
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    let mx = 0, my = 0;

    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      mx = (e.clientX - w / 2) / w;
      my = (e.clientY - h / 2) / h;
      if (!raf) {
        raf = requestAnimationFrame(() => {
          el.style.setProperty("--mx", String(mx));
          el.style.setProperty("--my", String(my));
          raf = 0;
        });
      }
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [low]);

  const dots = low ? 14 : 28;

  return (
    <div ref={ref} className="absolute inset-0 will-change-transform" style={{ perspective: "1200px" }}>
      <div
        className="absolute -inset-[10%] -z-10"
        style={{
          transform: "translate3d(calc(var(--mx,0) * 20px), calc(var(--my,0) * 20px), 0)",
        }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            filter: low ? undefined : "drop-shadow(0 0 10px rgba(212,255,0,.06))",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 60% at 50% 20%, rgba(212,255,0,.08), transparent 45%), radial-gradient(40% 30% at 80% 80%, rgba(212,255,0,.06), transparent 50%)",
          }}
        />
      </div>

      {/* Particles (fewer on low-perf) */}
      {!low && (
        <div className="pointer-events-none absolute inset-0 -z-10">
          {Array.from({ length: dots }).map((_, i) => (
            <div
              key={i}
              className="absolute w-[2px] h-[2px] rounded-full bg-white/40"
              style={{
                left: `${(i * 131) % 100}%`,
                top: `${(i * 73) % 100}%`,
                animation: `floaty ${4 + (i % 5)}s ease-in-out ${(i % 10) * 0.2}s infinite`,
                filter: "drop-shadow(0 0 6px rgba(212,255,0,.5))",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** -----------------------------
 * HUD bits
 * ------------------------------*/
function HUDChrome() {
  return (
    <div
      className="pointer-events-none absolute -inset-0.5 rounded-2xl"
      style={{
        boxShadow:
          "0 0 0 1px rgba(255,255,255,.06), inset 0 0 0 1px rgba(255,255,255,.04), 0 0 30px rgba(212,255,0,.06)",
      }}
    />
  );
}
function HUDField({ icon, label, children }) {
  return (
    <div className="relative">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-400 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="relative">{children}</div>
    </div>
  );
}
function Legend({ className = "" }) {
  return (
    <div className={`min-w-0 max-w-full ${className}`}>
      <div className="flex flex-wrap items-center justify-end gap-2 text-xs text-neutral-400">
        <Badge color="bg-[--neon]" label="Available" />
        <Badge color="bg-red-500" label="Booked" />
        <Badge color="bg-yellow-400 text-black" label="Selected" />
      </div>
    </div>
  );
}
function Badge({ color, label }) {
  return (
    <span className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-black/50 border border-white/10 shrink-0">
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
      <span className="whitespace-nowrap">{label}</span>
    </span>
  );
}

/** -----------------------------
 * Date Picker (portal + outside click)
 * ------------------------------*/
function NeonDatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const today = new Date();
  const toISOlocal = toISO;
  const addDaysLocal = addDays;

  const [view, setView] = useState(
    value ? new Date(value + "T00:00:00") : new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const endOfMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0);
  const startWeekday = (startOfMonth.getDay() + 6) % 7;
  const days = endOfMonth.getDate();

  const grid = useMemo(() => {
    const g = [];
    for (let i = 0; i < startWeekday; i++) g.push(null);
    for (let d = 1; d <= days; d++) g.push(new Date(view.getFullYear(), view.getMonth(), d));
    return g;
  }, [startWeekday, days, view]);

  function pick(dateObj) {
    onChange(toISOlocal(dateObj));
    setOpen(false);
  }

  function computePos() {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = 288;
    const margin = 8;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const top = rect.bottom + margin;
    setPos({ top, left, width });
  }

  useEffect(() => {
    if (!open) return;
    computePos();
    const onScroll = () => computePos();
    const onResize = () => computePos();
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-lg bg-black/60 border border-white/10 p-2 text-left text-white focus:ring-2 focus:ring-[--neon] outline-none flex items-center justify-between"
      >
        <span>{value || "Select date"}</span>
        <Calendar className="w-4 h-4 text-[--neon]" />
      </button>

      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9999] bg-black/0" onMouseDown={() => setOpen(false)} />
            <div
              className="fixed z-[10000] w-72 rounded-xl border border-white/10 bg-black/80 backdrop-blur-xl shadow-[0_0_30px_rgba(212,255,0,.15)] p-3"
              style={{ top: pos.top, left: pos.left, width: pos.width || undefined }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                  className="px-2 py-1 rounded bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10"
                >
                  ‹
                </button>
                <div className="text-sm font-semibold">
                  {view.toLocaleString(undefined, { month: "long", year: "numeric" })}
                </div>
                <button
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                  className="px-2 py-1 rounded bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10"
                >
                  ›
                </button>
              </div>

              <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-neutral-400 mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => (
                  <div key={w} className="text-center py-1">
                    {w}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {grid.map((d, i) => {
                  const todayFloor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const isDisabled = !d || d < todayFloor;
                  const isSelected = d && value === toISOlocal(d);
                  return (
                    <button
                      key={i}
                      disabled={!d || isDisabled}
                      onClick={() => d && pick(d)}
                      className={`aspect-square rounded-lg text-sm border transition ${!d
                        ? "invisible"
                        : isDisabled
                          ? "text-neutral-600 border-transparent"
                          : isSelected
                            ? "bg-[--neon] text-black font-bold border-[--neon]"
                            : "bg-white/5 text-neutral-200 border-white/10 hover:border-[--neon]/60"
                        }`}
                    >
                      {d ? d.getDate() : ""}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => pick(new Date())}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/10 hover:border-[--neon]/60"
                >
                  Today
                </button>
                <button
                  onClick={() => pick(addDaysLocal(new Date(), 1))}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/10 hover:border-[--neon]/60"
                >
                  Tomorrow
                </button>
                <button
                  onClick={() => {
                    const d = new Date();
                    const delta = (6 - d.getDay() + 7) % 7;
                    return pick(addDaysLocal(d, delta));
                  }}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/10 hover:border-[--neon]/60"
                >
                  Weekend
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

/** -----------------------------
 * Floor SVG (memoized, color-synced to legend)
 * ------------------------------*/
const FloorSVG = ({ availablePCs, selectedPC, onSelect, date, startTime, endTime, iconScale = 1 }) => {
  const isReady = date && startTime && endTime;

  return (
    <svg viewBox="0 0 100 60" className="w-full h-[420px] md:h-[600px] block bg-black/60">
      {/* walls */}
      <rect
        x="1"
        y="1"
        width="98"
        height="58"
        rx="2"
        fill="url(#g0)"
        stroke="rgba(255,255,255,.12)"
        strokeWidth="0.6"
      />
      {/* bar / desk zones */}
      <rect
        x="4"
        y="42"
        width="92"
        height="14"
        rx="1.5"
        fill="rgba(255,255,255,.03)"
        stroke="rgba(255,255,255,.08)"
        strokeWidth="0.4"
      />
      <rect
        x="4"
        y="4"
        width="92"
        height="10"
        rx="1.5"
        fill="rgba(255,255,255,.03)"
        stroke="rgba(255,255,255,.08)"
        strokeWidth="0.4"
      />
      {/* decor lights */}
      <circle cx="8" cy="8.5" r="0.8" fill={NEON} opacity=".6" />
      <circle cx="92" cy="8.5" r="0.8" fill={NEON} opacity=".6" />
      <circle cx="8" cy="49" r="0.8" fill={NEON} opacity=".6" />
      <circle cx="92" cy="49" r="0.8" fill={NEON} opacity=".6" />

      {/* rigs */}
      {PCS.map((pc) => {
        const p = RIG_POS[pc];
        const available = availablePCs.includes(pc);
        const selected = selectedPC === pc;

        const monitorSize = 6 * iconScale;     // was 7.4
        const monitorHalf = monitorSize / 2;
        const labelFont = 3 * iconScale;

        // Map to the legend states
        const state = !isReady ? "idle" : !available ? "booked" : selected ? "selected" : "available";

        // Pod fill/stroke by state
        let fill, stroke, ringStroke, labelFill;
        switch (state) {
          case "booked":
            fill = "rgba(239,68,68,.85)"; // red-500
            stroke = RED;
            ringStroke = RED;
            labelFill = "white";
            break;
          case "selected":
            fill = "rgba(250,204,21,.85)"; // yellow-400
            stroke = YELLOW;
            ringStroke = YELLOW;
            labelFill = "black"; // match badge: yellow bg, black text
            break;
          case "available":
            fill = "rgba(212,255,0,.22)"; // neon tint
            stroke = NEON;
            ringStroke = NEON;
            labelFill = NEON;
            break;
          default: // idle (when no date/time yet)
            fill = "rgba(255,255,255,.16)";
            stroke = "rgba(255,255,255,.28)";
            ringStroke = NEON;
            labelFill = "rgba(255,255,255,.85)";
        }

        const clickable = state !== "booked";

        return (
          <g
            key={pc}
            onClick={() => clickable && onSelect(pc)}
            className={clickable ? "cursor-pointer" : "cursor-not-allowed"}
            transform={`translate(${p.x}, ${p.y})`}
          >
            {/* selection ring only when selected */}
            {state === "selected" && (
              <circle
                cx="0" cy="0" r="6.8"
                fill="none"
                stroke={ringStroke}
                strokeWidth="0.9"
                style={{ filter: "drop-shadow(0 0 4px rgba(250,204,21,.8))" }}
              />
            )}

            {/* pod */}
            <circle
              cx="0" cy="0" r="6"
              fill={fill}
              stroke={stroke}
              strokeWidth="0.8"
              style={{
                transition: "all .18s ease",
                filter:
                  state === "booked"
                    ? "drop-shadow(0 0 5px rgba(239,68,68,.5))"
                    : state === "selected"
                      ? "drop-shadow(0 0 6px rgba(250,204,21,.6))"
                      : state === "available"
                        ? "drop-shadow(0 0 5px rgba(212,255,0,.35))"
                        : "drop-shadow(0 0 3px rgba(255,255,255,.15))",
              }}
            />

            {/* icon */}
            <Monitor
              x={-monitorHalf}
              y={-monitorHalf}
              width={monitorSize}
              height={monitorSize}
              color={selected ? NEON : "white"}
              opacity={selected ? 1 : 0.85}
            />

            {/* label under pod */}
            <text
              x="0"
              y="11.2"
              textAnchor="middle"
              fontSize={labelFont}
              fill={selected ? NEON : "rgba(255,255,255,.85)"}
              style={{ letterSpacing: ".04em", fontWeight: 700 }}
            >
              {pc}
            </text>

            {/* chip: AVAILABLE / BOOKED / SELECTED with legend colors */}
            {isReady && (
              <>
                <rect
                  x="-10" y="-14" rx="1.8" ry="1.8"
                  width="20" height="6"
                  fill={state === "booked" ? RED : state === "selected" ? YELLOW : NEON}
                  stroke={state === "booked" ? RED : state === "selected" ? YELLOW : NEON}
                  strokeWidth="0.3"
                />
                <text
                  x="0" y="-10.3"
                  textAnchor="middle"
                  fontSize="2.4"
                  fill={state === "booked" ? "white" : "black"}
                  style={{ fontWeight: 900 }}
                >
                  {state === "booked" ? "BOOKED" : state === "selected" ? "SELECTED" : "AVAILABLE"}
                </text>
              </>
            )}
          </g>
        );
      })}

      <defs>
        <linearGradient id="g0" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,.04)" />
          <stop offset="100%" stopColor="rgba(255,255,255,.01)" />
        </linearGradient>
      </defs>
    </svg>
  );
};
const MemoFloorSVG = React.memo(FloorSVG);
